import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { CalendarClock, Check, ExternalLink, Loader2, Mail } from "lucide-react";

/* ----------------------- Upcoming Sessions view -----------------------
 * Pulls /api/admin/appointments — Calendly bookings that came in via the
 * webhook. Groups into TODAY, TOMORROW, and LATER so Ashlee sees what
 * matters at the top. Per-row buttons to open the video room and email
 * the caregiver directly.
 */
export default function UpcomingAppointmentsView() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");

    useEffect(() => {
        let active = true;
        api.get("/admin/appointments").then((r) => {
            if (!active) return;
            setRows(r.data || []);
        }).catch((e) => {
            if (!active) return;
            setErr(e?.response?.data?.detail || "Couldn't load appointments.");
        }).finally(() => {
            if (active) setLoading(false);
        });
        return () => { active = false; };
    }, []);

    // Bucket by today / tomorrow / later (caregiver's locale).
    const now = new Date();
    const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
    const today = startOfDay(now);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(today); dayAfter.setDate(dayAfter.getDate() + 2);

    const buckets = { today: [], tomorrow: [], later: [] };
    rows.forEach((r) => {
        const t = new Date(r.start_time);
        if (t >= today && t < tomorrow) buckets.today.push(r);
        else if (t >= tomorrow && t < dayAfter) buckets.tomorrow.push(r);
        else if (t >= dayAfter) buckets.later.push(r);
    });

    return (
        <section data-testid="admin-appointments">
            <header className="mb-6">
                <h1 className="font-heading text-2xl md:text-3xl text-[#12776F] inline-flex items-center gap-2">
                    <CalendarClock size={22} strokeWidth={1.7} className="text-teal-dark" />
                    Upcoming sessions
                </h1>
                <p className="text-sm text-[#4C5551] mt-1 max-w-2xl">
                    Caregiver-booked Calendly sessions. They appear here automatically when a caregiver picks a time.
                    Each gets a 24-hour reminder email the day before.
                </p>
            </header>

            {loading ? (
                <p className="text-sm text-[#6E7A74] inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Loading…
                </p>
            ) : err ? (
                <p className="text-sm text-coral-dark">{err}</p>
            ) : rows.length === 0 ? (
                <div className="ml-card p-7 bg-cream-light text-center" data-testid="appointments-empty">
                    <CalendarClock size={28} strokeWidth={1.6} className="text-teal-dark mx-auto mb-2" />
                    <p className="text-sm text-[#1F2624] font-medium">No upcoming sessions yet.</p>
                    <p className="text-sm text-[#4C5551] mt-1 max-w-md mx-auto">
                        When a caregiver books on Calendly, the webhook records them here.
                        Make sure your Calendly webhook is pointed at
                        <code className="font-mono text-xs bg-white px-1.5 py-0.5 rounded mx-1">/api/webhook/calendly</code>
                        and that <code className="font-mono text-xs">CALENDLY_WEBHOOK_SECRET</code> is set in production env.
                    </p>
                </div>
            ) : (
                <div className="space-y-6">
                    <AppointmentGroup label="Today" rows={buckets.today} tone="coral" emptyHint="Nothing scheduled today — go take a breath." />
                    <AppointmentGroup label="Tomorrow" rows={buckets.tomorrow} tone="yellow" emptyHint="No bookings tomorrow yet." />
                    <AppointmentGroup label="Later" rows={buckets.later} tone="teal" emptyHint="" />
                </div>
            )}
        </section>
    );
}

function AppointmentGroup({ label, rows, tone, emptyHint }) {
    const tones = {
        coral: "bg-coral-light/40 border-coral-softer text-coral-dark",
        yellow: "bg-yellow-soft/40 border-yellow-soft text-yellow-dark",
        teal: "bg-teal-soft/40 border-teal-light text-teal-dark",
    };
    if (rows.length === 0 && !emptyHint) return null;
    return (
        <div data-testid={`appointments-group-${label.toLowerCase()}`}>
            <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-2xl border text-xs font-semibold uppercase tracking-wider mb-3 ${tones[tone]}`}>
                {label}{rows.length > 0 && ` · ${rows.length}`}
            </div>
            {rows.length === 0 ? (
                <p className="text-sm text-[#6E7A74] italic ml-1">{emptyHint}</p>
            ) : (
                <ul className="space-y-2">
                    {rows.map((r, i) => <AppointmentRow key={r.calendly_event_uri || `${r.invitee_email}-${i}`} appt={r} />)}
                </ul>
            )}
        </div>
    );
}

function AppointmentRow({ appt }) {
    const start = appt.start_time ? new Date(appt.start_time) : null;
    const whenStr = start
        ? start.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
        : "—";
    const doxy = process.env.REACT_APP_DOXY_ROOM_URL || "https://doxy.me/npashlee";
    const inProgress = start && Math.abs(start.getTime() - Date.now()) < 30 * 60 * 1000;
    const mailto = `mailto:${appt.invitee_email}?subject=${encodeURIComponent(`Memory Lane — ${appt.event_name || "your session"}`)}`;

    return (
        <li
            data-testid={`appointment-row-${(appt.calendly_event_uri || appt.invitee_email).slice(-20)}`}
            className={`ml-card p-4 md:p-5 flex flex-wrap items-center gap-3 ${inProgress ? "bg-stage_early/30 border-stage_early" : "bg-white"}`}
        >
            <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-semibold text-[#1F2624]">
                    {appt.invitee_name || appt.invitee_email}
                    {inProgress && (
                        <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-stage_early-dark bg-white px-2 py-0.5 rounded-2xl">
                            Live now
                        </span>
                    )}
                </p>
                <p className="text-xs text-[#4C5551] mt-0.5">
                    {whenStr}{appt.timezone ? ` · ${appt.timezone}` : ""} · {appt.event_name || "Care session"}
                </p>
                <p className="text-xs text-[#6E7A74] mt-0.5 truncate">{appt.invitee_email}</p>
                {appt.reminded && (
                    <p className="text-[10px] text-stage_early-dark mt-0.5 inline-flex items-center gap-1">
                        <Check size={10} strokeWidth={2} /> Reminder sent {appt.reminded_at ? new Date(appt.reminded_at).toLocaleDateString() : ""}
                    </p>
                )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
                <a
                    href={doxy}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="appointment-join"
                    className="ml-btn-primary inline-flex items-center gap-1.5"
                >
                    Join now <ExternalLink size={12} />
                </a>
                <a
                    href={mailto}
                    data-testid="appointment-email"
                    className="ml-btn-secondary inline-flex items-center gap-1.5"
                >
                    <Mail size={12} /> Email
                </a>
            </div>
        </li>
    );
}




